import { useState, useEffect, useCallback } from "react";
import { ConfirmModal, ActionButtons, useToast } from "../../../../shared/components/SharedUI";
import { Link } from "react-router-dom";
import { useAsientos } from "../hooks/useAsientos";
import apiFetch from "../../../../shared/services/apiClient";
import styles from "../../invoices/styles/invoice.module.css";

/*
  Design tokens (del proyecto):
  bg: #121721   panel: #1f2937   side: #2a3545
  input: #344152   border: #364250
  text: #e5ecf5   muted: #8d98a7
  primary: #f97316   font: 14px
*/

const TIPO_COLOR = {
  MANUAL:  { bg: "rgba(167,139,250,0.1)", border: "rgba(167,139,250,0.3)", text: "#a78bfa", label: "Manual"  },
  FACTURA: { bg: "rgba(74,222,128,0.1)",  border: "rgba(74,222,128,0.3)",  text: "#4ade80", label: "Factura" },
  CXP:     { bg: "rgba(251,191,36,0.1)",  border: "rgba(251,191,36,0.3)",  text: "#fbbf24", label: "CxP"     },
  RECIBO:  { bg: "rgba(96,165,250,0.1)",  border: "rgba(96,165,250,0.3)",  text: "#60a5fa", label: "Recibo"  },
};

const fmt = (n) => Number(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 2 });

// ── Modal de detalle / edición ────────────────────────────────────────────────
function AsientoDetalleModal({ asientoId, onClose, onDeleted, onSaved }) {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [concepto, setConcepto] = useState("");
  const [lineas,   setLineas]   = useState([]);
  const [cuentas,  setCuentas]  = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirm,  setConfirm]  = useState(false);
  const [toastMsg, setToastMsg] = useState({ msg:"", ok:true });

  const showT = (msg, ok=true) => { setToastMsg({msg,ok}); setTimeout(()=>setToastMsg({msg:"",ok:true}),3500); };

  function loadAsiento() {
    setLoading(true);
    apiFetch(`/api/finance/asientos/${asientoId}/`)
      .then(r=>r.json())
      .then(d=>{
        setData(d); setConcepto(d.concepto);
        setLineas(d.lineas?.map(l=>({
          cuenta:l.cuenta, cuenta_codigo:l.cuenta_codigo, cuenta_nombre:l.cuenta_nombre,
          debito:Number(l.debito)||0, credito:Number(l.credito)||0, descripcion:l.descripcion||"",
        }))||[]);
      })
      .finally(()=>setLoading(false));
  }

  useEffect(()=>{
    loadAsiento();
    apiFetch("/api/finance/catalogo/?is_detalle=true&page_size=1000&ordering=codigo")
      .then(r=>r.json()).then(d=>setCuentas(d.results||d)).catch(()=>{});
  },[asientoId]);

  const totalD  = lineas.reduce((s,l)=>s+(l.debito||0),0);
  const totalH  = lineas.reduce((s,l)=>s+(l.credito||0),0);
  const balanced = Math.abs(totalD-totalH)<0.001;

  const updL=(idx,field,val)=>setLineas(ls=>ls.map((l,i)=>i===idx?{...l,[field]:val}:l));
  const addL=()=>setLineas(ls=>[...ls,{cuenta:null,cuenta_codigo:"",cuenta_nombre:"",debito:0,credito:0,descripcion:""}]);
  const delL=(idx)=>{ if(lineas.length<=2){showT("Mínimo 2 líneas",false);return;} setLineas(ls=>ls.filter((_,i)=>i!==idx)); };
  const chgC=(idx,id)=>{
    const c=cuentas.find(x=>String(x.id)===String(id));
    setLineas(ls=>ls.map((l,i)=>i===idx?{...l,cuenta:c?.id||null,cuenta_codigo:c?.codigo||"",cuenta_nombre:c?.nombre||""}:l));
  };

  async function handleSave() {
    if(!balanced){showT("Asiento no balanceado",false);return;}
    setSaving(true);
    try {
      const res = await apiFetch(`/api/finance/asientos/${asientoId}/`,{
        method:"PATCH",
        body:JSON.stringify({concepto,lineas:lineas.map(l=>({cuenta:l.cuenta,debito:l.debito||0,credito:l.credito||0,descripcion:l.descripcion||""}))}),
      });
      if(!res.ok){const e=await res.json();throw new Error(e.detail||JSON.stringify(e));}
      showT("Guardado correctamente ✓");
      setEditMode(false); loadAsiento(); onSaved?.();
    } catch(e){showT(e.message||"Error al guardar",false);}
    finally{setSaving(false);}
  }

  async function handleDelete() {
    setDeleting(true);
    try{ await apiFetch(`/api/finance/asientos/${asientoId}/`,{method:"DELETE"}); onDeleted?.(); onClose(); }
    catch(_){}finally{setDeleting(false);}
  }

  const inpSt={background:"#1a2535",border:"1px solid #364250",borderRadius:5,color:"#e5ecf5",fontSize:13,padding:"5px 8px",outline:"none",width:"100%",boxSizing:"border-box"};

  return (
    <div style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(0,0,0,0.75)",backdropFilter:"blur(3px)",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={onClose}>
      <div style={{background:"#1f2937",border:"1px solid #364250",borderRadius:8,width:"min(840px,96vw)",maxHeight:"92vh",display:"flex",flexDirection:"column",overflow:"hidden"}} onClick={e=>e.stopPropagation()}>
        {loading ? (
          <div style={{textAlign:"center",padding:48,color:"#8d98a7"}}>
            <i className="fa-solid fa-circle-notch fa-spin" style={{color:"#f97316",fontSize:"1.4rem",display:"block",marginBottom:8}}/>Cargando…
          </div>
        ) : data ? (<>
          {/* Header */}
          <div style={{padding:"16px 20px",borderBottom:"1px solid #364250",display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexShrink:0}}>
            <div>
              <h2 style={{margin:0,fontSize:16,fontWeight:600,color:"#e5ecf5",fontFamily:"monospace"}}>{data.numero}</h2>
              <p style={{margin:"3px 0 0",color:"#8d98a7",fontSize:13}}>{data.fecha} · {data.tipo_display}</p>
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              {!editMode&&(<button type="button" onClick={()=>setEditMode(true)}
                style={{padding:"6px 12px",background:"transparent",border:"1px solid #364250",borderRadius:6,color:"#8d98a7",cursor:"pointer",fontSize:13,display:"flex",alignItems:"center",gap:6}}
                onMouseOver={e=>{e.currentTarget.style.borderColor="#f97316";e.currentTarget.style.color="#f97316";}}
                onMouseOut={e=>{e.currentTarget.style.borderColor="#364250";e.currentTarget.style.color="#8d98a7";}}>
                <i className="fa-solid fa-pen-to-square"/> Editar
              </button>)}
              {!editMode&&(!confirm?(
                <button type="button" onClick={()=>setConfirm(true)}
                  style={{padding:"6px 12px",background:"transparent",border:"1px solid #364250",borderRadius:6,color:"#8d98a7",cursor:"pointer",fontSize:13,display:"flex",alignItems:"center",gap:6}}
                  onMouseOver={e=>{e.currentTarget.style.borderColor="#ef4444";e.currentTarget.style.color="#f87171";}}
                  onMouseOut={e=>{e.currentTarget.style.borderColor="#364250";e.currentTarget.style.color="#8d98a7";}}>
                  <i className="fa-solid fa-trash"/> Eliminar
                </button>
              ):(
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <span style={{color:"#f87171",fontSize:12}}>¿Eliminar?</span>
                  <button type="button" onClick={handleDelete} disabled={deleting}
                    style={{padding:"5px 10px",background:"rgba(239,68,68,0.15)",border:"1px solid #ef4444",borderRadius:6,color:"#f87171",cursor:"pointer",fontSize:12,fontWeight:600}}>
                    {deleting?<i className="fa-solid fa-circle-notch fa-spin"/>:"Sí, eliminar"}
                  </button>
                  <button type="button" onClick={()=>setConfirm(false)}
                    style={{padding:"5px 10px",background:"transparent",border:"1px solid #364250",borderRadius:6,color:"#8d98a7",cursor:"pointer",fontSize:12}}>
                    Cancelar
                  </button>
                </div>
              ))}
              <button type="button" onClick={onClose}
                style={{background:"none",border:"none",color:"#8d98a7",cursor:"pointer",fontSize:"1.1rem",padding:"4px 6px"}}>
                <i className="fa-solid fa-xmark"/>
              </button>
            </div>
          </div>

          {/* Toast */}
          {toastMsg.msg&&(
            <div style={{padding:"8px 20px",background:toastMsg.ok?"rgba(74,222,128,0.08)":"rgba(239,68,68,0.08)",borderBottom:"1px solid #364250",color:toastMsg.ok?"#4ade80":"#f87171",fontSize:13,display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
              <i className={`fa-solid ${toastMsg.ok?"fa-circle-check":"fa-circle-exclamation"}`}/>{toastMsg.msg}
            </div>
          )}

          {/* Aviso tipo automático */}
          {editMode&&data.tipo!=="MANUAL"&&(
            <div style={{padding:"10px 20px",background:"rgba(251,191,36,0.08)",borderBottom:"1px solid rgba(251,191,36,0.2)",fontSize:12,color:"#fbbf24",display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
              <i className="fa-solid fa-triangle-exclamation"/>
              Asiento <strong>{data.tipo_display}</strong> — Al guardar, el monto del documento origen se actualizará automáticamente.
            </div>
          )}

          {/* Concepto */}
          <div style={{padding:"12px 20px",borderBottom:"1px solid #364250",flexShrink:0}}>
            {editMode?(
              <div>
                <label style={{fontSize:11,color:"#8d98a7",textTransform:"uppercase",letterSpacing:"0.5px",display:"block",marginBottom:4}}>Concepto</label>
                <input type="text" value={concepto} onChange={e=>setConcepto(e.target.value)} style={{...inpSt,border:"1px solid #f97316"}}/>
              </div>
            ):(
              <p style={{margin:0,color:"#e5ecf5",fontSize:14}}>{data.concepto}</p>
            )}
          </div>

          {/* Tabla líneas */}
          <div style={{flex:1,overflowY:"auto",scrollbarWidth:"thin",scrollbarColor:"#364250 #1f2937"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
              <thead style={{position:"sticky",top:0,background:"#1f2937",zIndex:1}}>
                <tr style={{borderBottom:"1px solid #364250"}}>
                  {["Cuenta","Descripción","Debe","Haber"].map(h=>(
                    <th key={h} style={{padding:"10px 12px",color:"#8d98a7",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.5px",textAlign:["Debe","Haber"].includes(h)?"right":"left"}}>{h}</th>
                  ))}
                  {editMode&&<th style={{width:32}}/>}
                </tr>
              </thead>
              <tbody>
                {lineas.map((l,i)=>(
                  <tr key={i} style={{background:i%2===0?"transparent":"rgba(55,65,81,0.08)",borderBottom:"1px solid #364250"}}>
                    <td style={{padding:"6px 8px"}}>
                      {editMode?(
                        <select value={l.cuenta||""} onChange={e=>chgC(i,e.target.value)} style={inpSt}>
                          <option value="">{cuentas.length===0?"Cargando cuentas…":"— Seleccionar cuenta —"}</option>
                          {l.cuenta&&!cuentas.find(c=>String(c.id)===String(l.cuenta))&&(
                            <option value={l.cuenta}>{l.cuenta_codigo} — {l.cuenta_nombre}</option>
                          )}
                          {cuentas.map(c=><option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>)}
                        </select>
                      ):(
                        <><span style={{fontFamily:"monospace",color:"#f97316",fontSize:11}}>{l.cuenta_codigo}</span>
                        <span style={{color:"#8d98a7",marginLeft:8}}>{l.cuenta_nombre}</span></>
                      )}
                    </td>
                    <td style={{padding:"6px 8px"}}>
                      {editMode?(
                        <input type="text" value={l.descripcion} style={inpSt} onChange={e=>updL(i,"descripcion",e.target.value)} placeholder="Descripción"/>
                      ):(
                        <span style={{padding:"9px 12px",color:"#8d98a7"}}>{l.descripcion||"—"}</span>
                      )}
                    </td>
                    <td style={{padding:"6px 8px"}}>
                      {editMode?(
                        <input type="number" min="0" step="0.01" value={l.debito||""} style={{...inpSt,textAlign:"right",color:"#4ade80"}}
                          onChange={e=>{updL(i,"debito",Number(e.target.value)||0);if(Number(e.target.value)>0)updL(i,"credito",0);}}/>
                      ):(
                        <div style={{padding:"9px 12px",fontFamily:"monospace",textAlign:"right",color:l.debito>0?"#4ade80":"#364250"}}>
                          {l.debito>0?`₡${fmt(l.debito)}`:"—"}
                        </div>
                      )}
                    </td>
                    <td style={{padding:"6px 8px"}}>
                      {editMode?(
                        <input type="number" min="0" step="0.01" value={l.credito||""} style={{...inpSt,textAlign:"right",color:"#60a5fa"}}
                          onChange={e=>{updL(i,"credito",Number(e.target.value)||0);if(Number(e.target.value)>0)updL(i,"debito",0);}}/>
                      ):(
                        <div style={{padding:"9px 12px",fontFamily:"monospace",textAlign:"right",color:l.credito>0?"#60a5fa":"#364250"}}>
                          {l.credito>0?`₡${fmt(l.credito)}`:"—"}
                        </div>
                      )}
                    </td>
                    {editMode&&(
                      <td style={{padding:"4px",textAlign:"center"}}>
                        <button type="button" onClick={()=>delL(i)}
                          style={{background:"none",border:"none",color:"#f87171",cursor:"pointer",fontSize:14,padding:"2px 6px"}}>
                          <i className="fa-solid fa-times"/>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {editMode&&(
                  <tr><td colSpan={5} style={{padding:"6px 8px"}}>
                    <button type="button" onClick={addL}
                      style={{background:"transparent",border:"1px dashed #364250",borderRadius:6,color:"#8d98a7",cursor:"pointer",padding:"5px 12px",fontSize:12,width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                      <i className="fa-solid fa-plus"/> Agregar línea
                    </button>
                  </td></tr>
                )}
                <tr style={{borderTop:"2px solid #364250",background:"rgba(55,65,81,0.15)"}}>
                  <td colSpan={2} style={{padding:"10px 12px",color:"#8d98a7",fontSize:11,fontWeight:700,textTransform:"uppercase"}}>TOTALES</td>
                  <td><div style={{padding:"10px 12px",textAlign:"right",color:"#4ade80",fontFamily:"monospace",fontWeight:700}}>₡{fmt(editMode?totalD:data.total_debitos)}</div></td>
                  <td><div style={{padding:"10px 12px",textAlign:"right",color:"#60a5fa",fontFamily:"monospace",fontWeight:700}}>₡{fmt(editMode?totalH:data.total_creditos)}</div></td>
                  {editMode&&<td/>}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Footer */}
          <div style={{padding:"12px 20px",borderTop:"1px solid #364250",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,fontSize:13,background:(editMode?balanced:data.balanceado)?"rgba(74,222,128,0.05)":"rgba(239,68,68,0.05)"}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <i className={`fa-solid ${(editMode?balanced:data.balanceado)?"fa-circle-check":"fa-triangle-exclamation"}`}
                 style={{color:(editMode?balanced:data.balanceado)?"#4ade80":"#f87171"}}/>
              <span style={{color:(editMode?balanced:data.balanceado)?"#4ade80":"#f87171"}}>
                {(editMode?balanced:data.balanceado)?"Asiento balanceado ✓":"Asiento NO balanceado"}
              </span>
              {editMode&&!balanced&&<span style={{color:"#f87171",fontSize:12}}>(Dif: ₡{fmt(Math.abs(totalD-totalH))})</span>}
            </div>
            {editMode&&(
              <div style={{display:"flex",gap:8}}>
                <button type="button" onClick={()=>{setEditMode(false);loadAsiento();}}
                  style={{padding:"7px 14px",background:"transparent",border:"1px solid #364250",borderRadius:6,color:"#8d98a7",cursor:"pointer",fontSize:13}}>
                  Cancelar
                </button>
                <button type="button" onClick={handleSave} disabled={saving||!balanced}
                  style={{padding:"7px 16px",background:balanced?"#f97316":"#374151",border:"none",borderRadius:6,color:balanced?"#fff":"#6b7280",cursor:balanced?"pointer":"not-allowed",fontSize:13,fontWeight:600,display:"flex",alignItems:"center",gap:6}}>
                  {saving?<><i className="fa-solid fa-circle-notch fa-spin"/>Guardando…</>:<><i className="fa-solid fa-floppy-disk"/>Guardar cambios</>}
                </button>
              </div>
            )}
          </div>
        </>):(
          <div style={{padding:40,textAlign:"center",color:"#f87171"}}>No se pudo cargar el asiento.</div>
        )}
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function ListAsientos() {
  const { showToast, toastEl } = useToast();
  const [page,     setPage]     = useState(1);
  const [search,   setSearch]   = useState("");
  const [tipo,     setTipo]     = useState("");
  const [detailId, setDetailId] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);

  const params = { page, ...(search && { search }), ...(tipo && { tipo }) };
  const { asientos, count, loading, error, refetch } = useAsientos(params);
  const totalPages = Math.max(1, Math.ceil(count / 20));

  function showMsg(ok, text) {
    setActionMsg({ ok, text });
    setTimeout(() => setActionMsg(null), 4000);
  }

  return (
    <>
      {/* Breadcrumb */}
      
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>Inicio /</span>
        </Link>
          <span className={styles.active}> Asientos de Diario</span>
      </span>

      {/* Header */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "flex-start", flexWrap: "wrap", gap: 12, margin: "10px 0 20px",
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "#e5ecf5",
            display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ background: "rgba(249,115,22,0.2)", color: "#f97316",
              padding: "6px 8px", borderRadius: 8 }}>
              <i className="fa-solid fa-book-open" style={{color:"#f97316"}}/>
            </span>
            Asientos de Diario
            
          </h2>
          
          <p style={{ margin: "6px 0 0", color: "#8d98a7", fontSize: 13 }}>
            {count} asiento{count !== 1 ? "s" : ""} registrado{count !== 1 ? "s" : ""}
          </p>  
        </div>
        <Link to="/finanzas/asientos/nuevo"
          className="btn"
          style={{
            padding: "9px 18px", background: "#f97316", borderRadius: 6,
            color: "#fff", textDecoration: "none", fontWeight: 600,
            fontSize: 14, display: "flex", alignItems: "center", gap: 7,
            border: "none",
          }}>
          <i className="fa-solid fa-plus" /> Nuevo Asiento Manual
        </Link>
      </div>

      {/* Banner acción */}
      {actionMsg && (
        <div style={{
          padding: "10px 16px", marginBottom: 14, borderRadius: 6,
          background: actionMsg.ok ? "rgba(74,222,128,0.07)" : "rgba(239,68,68,0.07)",
          border: `1px solid ${actionMsg.ok ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}`,
          borderLeft: `4px solid ${actionMsg.ok ? "#4ade80" : "#f87171"}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{ color: actionMsg.ok ? "#4ade80" : "#f87171", fontSize: 14, fontWeight: 500 }}>
            <i className={`fa-solid ${actionMsg.ok ? "fa-circle-check" : "fa-triangle-exclamation"}`}
               style={{ marginRight: 8 }} />
            {actionMsg.text}
          </span>
          <button onClick={() => setActionMsg(null)}
            style={{ background: "none", border: "none", color: "#8d98a7", cursor: "pointer" }}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
      )}

      {/* Tabla contenedor */}
      <div className={styles["table-container"]}>

        {/* Filtros */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 14px", borderBottom: "1px solid #364250",
          background: "rgba(55,65,81,0.1)",
        }}>
          <i className="fa-solid fa-magnifying-glass" style={{ color: "#8d98a7", fontSize: 12 }} />
          <input type="text"
            placeholder="Buscar por número o concepto…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{
              flex: 1, background: "none", border: "none",
              color: "#e5ecf5", fontSize: 14, outline: "none",
            }} />
          <select value={tipo}
            onChange={(e) => { setTipo(e.target.value); setPage(1); }}
            style={{
              background: "#344152", border: "1px solid #364250",
              borderRadius: 6, color: tipo ? "#f97316" : "#8d98a7",
              padding: "5px 10px", fontSize: 13, cursor: "pointer", outline: "none",
            }}>
            <option value="">Todos los tipos</option>
            <option value="MANUAL">Manual</option>
            <option value="FACTURA">Factura</option>
            <option value="CXP">CxP</option>
            <option value="RECIBO">Recibo</option>
          </select>
        </div>

        {/* Tabla */}
        <div className={styles["table-responsive"]}>
          <div className={styles["table-body"]}>
            <table>
              <thead>
                <tr>
                  {["Número", "Fecha", "Concepto", "Tipo", "Débitos", "Créditos", "Estado", ""].map((h, i) => (
                    <th key={i} className="" style={{
                      textAlign: ["Débitos","Créditos"].includes(h) ? "right" : undefined,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: 40 }}>
                    <i className="fa-solid fa-circle-notch fa-spin"
                       style={{ color: "#f97316", fontSize: "1.2rem", display: "block", marginBottom: 8 }} />
                    Cargando asientos…
                  </td></tr>
                ) : error ? (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: 24, color: "#f87171" }}>
                    {error}
                  </td></tr>
                ) : asientos.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: "#8d98a7" }}>
                    No hay asientos que mostrar.
                  </td></tr>
                ) : asientos.map((a, i) => {
                  const color = TIPO_COLOR[a.tipo] || TIPO_COLOR.MANUAL;
                  return (
                    <tr key={a.id}>
                      <td style={{ fontFamily: "monospace", color: "#f97316", fontWeight: 600, fontSize: 13 }}>
                        {a.numero}
                      </td>
                      <td style={{ whiteSpace: "nowrap", fontSize: 13 }}>{a.fecha}</td>
                      <td style={{ maxWidth: 240, overflow: "hidden",
                        textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13 }}>
                        {a.concepto}
                      </td>
                      <td>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: "2px 8px",
                          borderRadius: 20, background: color.bg,
                          border: `1px solid ${color.border}`, color: color.text,
                        }}>
                          {color.label}
                        </span>
                      </td>
                      <td>
                        <div style={{ textAlign: "right", fontFamily: "monospace", color: "#4ade80", fontSize: 13 }}>
                          ₡{fmt(a.total_debitos)}
                        </div>
                      </td>
                      <td>
                        <div style={{ textAlign: "right", fontFamily: "monospace", color: "#60a5fa", fontSize: 13 }}>
                          ₡{fmt(a.total_creditos)}
                        </div>
                      </td>
                      <td>
                        {a.balanceado
                          ? <i className="fa-solid fa-circle-check"
                               style={{ color: "#4ade80" }} title="Balanceado" />
                          : <i className="fa-solid fa-triangle-exclamation"
                               style={{ color: "#f87171" }} title="No balanceado" />
                        }
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          <button type="button" onClick={() => setDetailId(a.id)}
                            title="Ver / Editar"
                            style={{ background: "none", border: "none",
                              color: "#8d98a7", cursor: "pointer", fontSize: 14,
                              padding: "4px 6px", transition: "color 0.2s" }}
                            onMouseOver={(e) => (e.currentTarget.style.color = "#f97316")}
                            onMouseOut={(e)  => (e.currentTarget.style.color = "#8d98a7")}
                          >
                            <i className="fa-solid fa-eye" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Paginación */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "10px 16px", borderTop: "1px solid #364250",
          background: "rgba(55,65,81,0.1)", fontSize: 13, color: "#8d98a7",
        }}>                
        <p style={{color:"#9ca3af", fontSize:"0.9rem"}}>
          <>
            <span>{count}</span> resultado(s) · Página <span>{page}</span> de{" "}
            <span>{Math.max(1, totalPages)}</span>
          </>
        </p>


          <div style={{ display: "flex", gap: 6 }}>
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              style={{
                padding: "5px 12px", fontSize: 13, cursor: page <= 1 ? "default" : "pointer",
                background: "transparent", borderRadius: 6,
                border: "1px solid #364250",
                color: page <= 1 ? "#364250" : "#8d98a7",
                transition: "all 0.2s",
              }}
              onMouseOver={(e) => { if (page > 1) e.currentTarget.style.color = "#e5ecf5"; }}
              onMouseOut={(e)  => { e.currentTarget.style.color = page <= 1 ? "#364250" : "#8d98a7"; }}
            >← Anterior</button>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
              style={{
                padding: "5px 12px", fontSize: 13,
                cursor: page >= totalPages ? "default" : "pointer",
                background: "transparent", borderRadius: 6,
                border: "1px solid #364250",
                color: page >= totalPages ? "#364250" : "#8d98a7",
                transition: "all 0.2s",
              }}
              onMouseOver={(e) => { if (page < totalPages) e.currentTarget.style.color = "#e5ecf5"; }}
              onMouseOut={(e)  => { e.currentTarget.style.color = page >= totalPages ? "#364250" : "#8d98a7"; }}
            >Siguiente →</button>
          </div>
        </div>
      </div>

      {/* Modal detalle / editar / eliminar */}
      {detailId && (
        <AsientoDetalleModal
          asientoId={detailId}
          onClose={() => setDetailId(null)}
          onDeleted={(msg) => { refetch(); showToast(msg || "Asiento eliminado correctamente."); }}
          onSaved={()   => { refetch(); showMsg(true, "Concepto actualizado correctamente."); }}
        />
      )}
    {toastEl}
    </>
  );
}
